import { Autocomplete, TextField, Typography } from '@mui/material';
import { Filter, useSearchkit } from '@searchkit/client';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { SkFacetSet, SkFacetSetEntry } from 'sr-types/lib/search/v1/graphql';
import { V } from '../../../common/Layout';
import { useI18n } from '../../../common/i18n/I18n';

export const SingleSelectFacet = ({
    facet,
    requiredFilters,
    autoApply,
    onFiltersChanged
}: {
    facet: SkFacetSet;
    requiredFilters: string[];
    autoApply?: boolean;
    onFiltersChanged?: () => void;
}) => {
    const api = useSearchkit();
    const placeholder = useI18n('filter.search', { label: facet.label });
    const isRequired = requiredFilters.includes(facet.identifier);
    const label = isRequired ? facet.label + ' *' : facet.label;

    // Local state for selection
    const [selected, setSelected] = useState<SkFacetSetEntry | null>(null);

    // Sync local selection with API filters when facet or filters change
    useEffect(() => {
        const filter: Filter[] = api.getFiltersByIdentifier(facet.identifier);
        if (filter?.length) {
            const entry = facet.entries.find((e: SkFacetSetEntry) => e.id.split('_')[1] === filter[0].value);
            setSelected(entry || null);
        } else {
            setSelected(null);
        }
    }, [api, facet.entries, facet.identifier, api.searchState.filters]);

    const handleChange = (event: SyntheticEvent, f: SkFacetSetEntry | null) => {
        const apiFilters = api.getFiltersByIdentifier(facet.identifier);
        if (f) {
            if (apiFilters?.length === 0 || apiFilters?.[0].identifier !== f?.id) {
                setSelected(f);
                api.removeFiltersByIdentifier(facet.identifier);
                api.addFilter({ identifier: facet.identifier, value: f?.id.split('_')[1] });
                onFiltersChanged?.();
                if (autoApply) {
                    api.search();
                }
            }
        } else {
            setSelected(null);
            api.removeFiltersByIdentifier(facet.identifier);
            onFiltersChanged?.();
            if (autoApply) {
                api.search();
            }
        }
    };

    return (
        <V>
            <Typography sx={{ fontSize: 'small' }}>{label}</Typography>
            <Autocomplete
                multiple={false}
                fullWidth
                disableClearable={isRequired}
                getOptionLabel={(option: SkFacetSetEntry) => option.label || ''}
                isOptionEqualToValue={(option: SkFacetSetEntry, value: SkFacetSetEntry) => option?.id === value?.id}
                options={facet.entries}
                renderOption={(props, option: SkFacetSetEntry, { selected }) => (
                    <li {...props} key={props.id}>
                        {option.label}
                    </li>
                )}
                renderInput={(params) => <TextField variant={'standard'} {...params} placeholder={placeholder} />}
                filterSelectedOptions
                value={selected}
                onChange={handleChange}
            />
        </V>
    );
};

SingleSelectFacet.DISPLAY = 'SingleSelectFacet';
